/**
 * 历史消息管理 Composable
 * 负责历史消息的加载、更新和清空操作
 */

import { ref, nextTick } from "vue";
import { useMessageFeedback } from "./useMessageFeedback";
import { useMessageSave } from "./useMessageSave";

export function useMessageHistory(
  initialMessages: any,
  messages: any,
  currentUserId: any,
  cacheActions?: {
    get: (sessionId?: string) => any[];
    set: (sessionId: string | undefined, messages: any[]) => void;
    clear: (sessionId?: string) => void;
  },
) {
  const { loadAndConvertMsg } = useMessageSave();
  const feedbackManager = useMessageFeedback();

  const isLoadingHistory = ref(false); // 加载历史记录状态
  let loadingSessionId = ""; // 当前正在加载的历史记录会话ID
  let lastFeedbackLoadKey = ""; // 上次加载反馈的会话ID，用于判断是否需要重新加载

  /**
   * 加载当前会话的历史消息
   */
  const loadHistoryMessages = async (sessionId: string) => {
    if (!sessionId || !currentUserId.value) return;
    if (isLoadingHistory.value && loadingSessionId === sessionId) return;

    const cachedMessages = cacheActions?.get(sessionId) || [];
    if (cachedMessages.length > 0) {
      // 会话切换优先走内存缓存，减少列表点击后等待数据库返回的空白时间。
      await updateMessagesState(cachedMessages, sessionId);
      return;
    }

    try {
      isLoadingHistory.value = true;
      loadingSessionId = sessionId;
      const restoredMessages = await loadAndConvertMsg(
        sessionId,
        currentUserId.value,
      );

      await updateMessagesState(restoredMessages || [], sessionId);
    } catch (error) {
      console.error("❌ 加载历史消息失败:", error);
    } finally {
      isLoadingHistory.value = false;
      loadingSessionId = "";
    }
  };

  /**
   * 更新消息状态（加载历史消息后调用）
   */
  const updateMessagesState = async (
    restoredMessages: any[],
    sessionId?: string,
    options: { loadFeedback?: boolean } = {},
  ) => {
    const { loadFeedback = true } = options;
    initialMessages.value = restoredMessages;

    // 等 useChat 完成内部同步后再写入，避免它的初始化流程覆盖历史消息。
    await nextTick();
    messages.value = [...restoredMessages];

    if (loadFeedback) {
      await loadAllMessageFeedbacks(sessionId);
    }
    cacheActions?.set(sessionId, messages.value);
  };

  /**
   * 批量加载所有AI消息的反馈状态（使用批量接口优化性能）
   */
  const loadAllMessageFeedbacks = async (sessionId?: string) => {
    if (!currentUserId.value || messages.value.length === 0) return;

    try {
      const assistantMessages = messages.value.filter(
        (msg: any) => msg.role === "assistant" && msg.id,
      );

      if (assistantMessages.length === 0) return;

      // 提取所有消息 ID
      const messageIds = assistantMessages.map((msg: any) => msg.id);
      const feedbackLoadKey = `${sessionId || ""}:${currentUserId.value}:${messageIds.join(",")}`;

      if (feedbackLoadKey === lastFeedbackLoadKey) {
        // 同一批消息不重复拉取反馈，避免切换/恢复时产生多余请求。
        return;
      }

      lastFeedbackLoadKey = feedbackLoadKey;

      console.log(`📊 加载 ${assistantMessages.length} 条消息的反馈状态...`);

      // 批量查询（1 次请求代替 N 次）
      await feedbackManager.loadBatchMessageFeedbacks(
        messageIds,
        currentUserId.value,
        messages.value,
      );

      console.log("✅ 所有消息的反馈状态加载完成");
    } catch (error) {
      console.error("❌ 批量加载反馈状态失败:", error);
    }
  };

  /**
   * 清空所有消息
   */
  const clearAllMessages = (sessionId?: string) => {
    initialMessages.value = [];
    messages.value = [];
    cacheActions?.clear(sessionId);
  };

  return {
    isLoadingHistory,
    loadHistoryMessages,
    updateMessagesState,
    clearAllMessages,
  };
}
